import {
  CONTACT_ROLE_LABELS, LIFECYCLE_LABELS, PRIORITY_LABELS, SOURCE_LABELS, TASK_KIND_LABELS, formatDate, formatDateTime, formatILS,
  formatRelativeDay, type LoggableActivityType,
} from '@takumi/shared';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle, Archive, ArrowRight, Banknote, Building2, FileText, Package, ShoppingBag, CalendarClock, CalendarPlus, Check, ChevronDown, Crown, Globe, Mail, MessageCircle, MoreHorizontal,
  NotebookPen, Pencil, Phone, Plus, Star, Trash2, Users,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Badge, Card, CardHeader, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  EmptyState, PageLoader, Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { safeDecodeUri } from '@/lib/utils';
import { useInvalidateCrm, useMoveStage, useOrg, useStages, useTimeline } from '@/lib/queries';
import type { Contact, OrgDetail, Task } from '@/lib/types';
import { FollowupBadge, PriorityDot, StageBadge } from '@/features/crm/badges';
import { ContactActions, PhoneText } from '@/features/crm/ContactActions';
import { LogActivityDialog } from '@/features/crm/LogActivityDialog';
import { CompleteTaskDialog, NewTaskDialog } from '@/features/crm/TaskDialogs';
import { Timeline } from '@/features/crm/Timeline';
import { ContactDialog } from '@/features/organizations/ContactDialog';
import { EditOrganizationDialog } from '@/features/organizations/EditOrganizationDialog';
import { LostReasonDialog } from '@/features/organizations/LostReasonDialog';
import { DocumentEditorDialog } from '@/features/sales/DocumentEditor';
import { OrgSalesPanel } from '@/features/sales/OrgSalesPanel';
import { PaymentDialog, SampleDialog } from '@/features/sales/PaymentDialogs';
import { SendMessageDialog } from '@/features/messaging/SendMessageDialog';
import { AttachmentsCard } from '@/features/operations/AttachmentsCard';

export function OrganizationPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { data: org, isLoading, error } = useOrg(id);
  const timeline = useTimeline(id);

  if (isLoading) return <PageLoader />;
  if (error || !org) return <EmptyState icon={<Building2 />} title="הרשומה לא נמצאה" action={<Button asChild variant="secondary"><Link to="/leads">חזרה ללידים</Link></Button>} />;

  return (
    <div className="mx-auto max-w-[1400px]">
      <OrgHeader org={org} />
      <div className="mt-5 grid gap-5 xl:grid-cols-[1fr_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <NextFollowupPanel org={org} />
          <Card className="overflow-hidden">
            <Tabs defaultValue="timeline">
              <TabsList className="px-3">
                <TabsTrigger value="timeline">ציר זמן</TabsTrigger>
                {can('sales.read') && <TabsTrigger value="sales">מכירות וכספים</TabsTrigger>}
                <TabsTrigger value="tasks">
                  משימות ופגישות {org.openTasks.length > 0 && <span className="rounded-full bg-sunken px-1.5 text-xs">{org.openTasks.length}</span>}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="timeline">
                <Timeline items={timeline.data} loading={timeline.isLoading} />
              </TabsContent>
              <TabsContent value="tasks">
                <OpenTasks org={org} />
              </TabsContent>
              {can('sales.read') && (
                <TabsContent value="sales">
                  <OrgSalesPanel orgId={org.id} />
                </TabsContent>
              )}
            </Tabs>
          </Card>
        </div>
        <aside className="flex flex-col gap-5">
          <ContactsCard org={org} />
          <AttachmentsCard orgId={org.id} />
          <BusinessCard org={org} />
          <CommercialCard org={org} />
          <AttributionCard org={org} />
        </aside>
      </div>
    </div>
  );
}

// ─── Header with stage, conversion and quick actions ─────────────────────
function OrgHeader({ org }: { org: OrgDetail }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const stages = useStages();
  const move = useMoveStage();
  const invalidate = useInvalidateCrm();
  const [logType, setLogType] = useState<LoggableActivityType | null>(null);
  const [taskKind, setTaskKind] = useState<'followup' | 'meeting' | 'task' | null>(null);
  const [editing, setEditing] = useState(false);
  const [lostStage, setLostStage] = useState<string | null>(null);
  const [salesDialog, setSalesDialog] = useState<null | 'quote' | 'order' | 'sample' | 'payment'>(null);
  const [sending, setSending] = useState<null | 'whatsapp' | 'email'>(null);
  const primary = org.contacts.find((c) => c.isPrimary) ?? org.contacts[0];
  const isLead = org.lifecycleStage === 'lead';
  const nextFollowup = org.openTasks.find((t) => t.kind === 'followup' || t.kind === 'call') ?? null;

  const archive = useMutation({
    mutationFn: () => api.post(`/organizations/${org.id}/archive`),
    onSuccess: () => {
      invalidate(org.id);
      toast.success('הועבר לארכיון');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/organizations/${org.id}`),
    onSuccess: () => {
      invalidate();
      toast.success('הרשומה נמחקה');
      navigate(isLead ? '/leads' : '/customers');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onStage = (stageId: string, kind: string) => {
    if (kind === 'lost') setLostStage(stageId);
    else move.mutate({ orgId: org.id, stageId });
  };

  return (
    <>
      <Link to={isLead ? '/leads' : '/customers'} className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
        <ArrowRight className="size-4" /> {isLead ? 'לידים' : 'לקוחות'}
      </Link>
      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="grid size-14 shrink-0 place-items-center rounded-card bg-matcha-800 font-display text-xl font-semibold text-matcha-100">
            {org.name.slice(0, 2)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[26px] leading-tight font-semibold">{org.name}</h1>
              <Badge variant={org.lifecycleStage === 'customer' ? 'gold' : 'matcha'}>
                {org.lifecycleStage === 'customer' && <Crown className="size-3" />}
                {LIFECYCLE_LABELS[org.lifecycleStage]}
              </Badge>
              {isLead && <StageBadge name={org.stageName} color={org.stageColor} />}
              <PriorityDot priority={org.priority} withLabel />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted">
              {org.businessType && <span>{org.businessType}</span>}
              {org.city && <span>{org.city}</span>}
              <span>מקור: {SOURCE_LABELS[org.source]}</span>
              <span>אחראי: {org.ownerName ?? 'לא משויך'}</span>
              {org.customerSince && <span>לקוח מאז {formatDate(org.customerSince)}</span>}
              {org.lastContactAt && <span>קשר אחרון {formatRelativeDay(org.lastContactAt)}</span>}
            </div>
          </div>
          {can('crm.write') && (
            <div className="flex flex-wrap items-center gap-2">
              {isLead && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary">
                      שלב: {org.stageName} <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>העברה לשלב</DropdownMenuLabel>
                    {stages.data
                      ?.filter((s) => s.isActive)
                      .map((s) => (
                        <DropdownMenuItem key={s.id} disabled={s.id === org.pipelineStageId} onSelect={() => onStage(s.id, s.kind)}>
                          <span className="size-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.name}
                          {s.kind === 'won' && <Crown className="ms-auto !text-gold-500" />}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isLead && (
                <Button
                  variant="gold"
                  loading={move.isPending}
                  onClick={() => {
                    const won = stages.data?.find((s) => s.kind === 'won' && s.isActive);
                    if (won) move.mutate({ orgId: org.id, stageId: won.id });
                  }}
                >
                  <Crown /> הפיכה ללקוח
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="icon" aria-label="פעולות נוספות">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onSelect={() => setEditing(true)}>
                    <Pencil /> עריכת פרטים
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTaskKind('meeting')}>
                    <CalendarPlus /> קביעת פגישה
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTaskKind('task')}>
                    <Check /> משימה חדשה
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {org.lifecycleStage !== 'archived' && (
                    <DropdownMenuItem onSelect={() => confirm('להעביר לארכיון? פולואפים פתוחים יבוטלו.') && archive.mutate()}>
                      <Archive /> העברה לארכיון
                    </DropdownMenuItem>
                  )}
                  {can('crm.delete') && (
                    <DropdownMenuItem className="text-danger" onSelect={() => confirm(`למחוק את "${org.name}"? הפעולה נרשמת ביומן.`) && remove.mutate()}>
                      <Trash2 /> מחיקה
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {can('crm.write') && (
          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4">
            <QuickAction icon={<Phone />} label="שיחה" onClick={() => setLogType('call')} href={primary?.phoneE164 ? `tel:${primary.phoneE164}` : undefined} />
            <QuickAction icon={<MessageCircle />} label="WhatsApp" onClick={() => setSending('whatsapp')} />
            <QuickAction icon={<Mail />} label="אימייל" onClick={() => setSending('email')} />
            <QuickAction icon={<Users />} label="פגישה" onClick={() => setLogType('meeting')} />
            <QuickAction icon={<NotebookPen />} label="הערה" onClick={() => setLogType('note')} />
            <QuickAction icon={<CalendarClock />} label="פולואפ" onClick={() => setTaskKind('followup')} />
            {can('sales.write') && (
              <>
                <span className="mx-1 h-6 w-px bg-line" />
                <QuickAction icon={<Package />} label="דוגמית" onClick={() => setSalesDialog('sample')} />
                <QuickAction icon={<FileText />} label="הצעת מחיר" onClick={() => setSalesDialog('quote')} />
                <QuickAction icon={<ShoppingBag />} label="הזמנה" onClick={() => setSalesDialog('order')} />
                <QuickAction icon={<Banknote />} label="תשלום" onClick={() => setSalesDialog('payment')} />
              </>
            )}
          </div>
        )}
      </Card>

      {logType && (
        <LogActivityDialog
          open
          onOpenChange={(o) => !o && setLogType(null)}
          orgId={org.id}
          orgName={org.name}
          contacts={org.contacts}
          openFollowup={nextFollowup}
          defaultType={logType}
        />
      )}
      <NewTaskDialog open={!!taskKind} onOpenChange={(o) => !o && setTaskKind(null)} organizationId={org.id} organizationName={org.name} contacts={org.contacts} defaultKind={taskKind ?? 'followup'} />
      <EditOrganizationDialog org={org} open={editing} onOpenChange={setEditing} />
      <DocumentEditorDialog kind="quote" open={salesDialog === 'quote'} onOpenChange={(o) => !o && setSalesDialog(null)} organizationId={org.id} organizationName={org.name} contacts={org.contacts} />
      <DocumentEditorDialog kind="order" open={salesDialog === 'order'} onOpenChange={(o) => !o && setSalesDialog(null)} organizationId={org.id} organizationName={org.name} contacts={org.contacts} />
      <SampleDialog open={salesDialog === 'sample'} onOpenChange={(o) => !o && setSalesDialog(null)} organizationId={org.id} contacts={org.contacts} />
      {sending && (
        <SendMessageDialog
          channel={sending}
          open
          onOpenChange={(o) => !o && setSending(null)}
          organizationId={org.id}
          organizationName={org.name}
          contacts={org.contacts}
          onFallbackLogged={() => setTimeout(() => setLogType(sending), 300)}
        />
      )}
      <PaymentDialog open={salesDialog === 'payment'} onOpenChange={(o) => !o && setSalesDialog(null)} organizationId={org.id} />
      <LostReasonDialog
        open={!!lostStage}
        onOpenChange={(o) => !o && setLostStage(null)}
        onConfirm={(reason) => {
          if (lostStage) move.mutate({ orgId: org.id, stageId: lostStage, lostReason: reason });
          setLostStage(null);
        }}
      />
    </>
  );
}

/** Opening tel:/wa.me/mailto and then logging what happened is one gesture. */
function QuickAction({ icon, label, onClick, href, external }: { icon: ReactNode; label: string; onClick: () => void; href?: string; external?: boolean }) {
  const cls = 'inline-flex h-9 items-center gap-1.5 rounded-control border border-line-strong bg-surface px-3 text-sm font-semibold text-ink-soft hover:border-matcha-400 hover:bg-matcha-50 hover:text-matcha-900 [&_svg]:size-4';
  if (href) {
    return (
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} className={cls} onClick={() => setTimeout(onClick, 300)}>
        {icon}
        {label}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}

// ─── Next follow-up: always visible ──────────────────────────────────────
function NextFollowupPanel({ org }: { org: OrgDetail }) {
  const { can } = useAuth();
  const [completing, setCompleting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const next = org.openTasks.find((t) => t.kind === 'followup' || t.kind === 'call' || t.kind === 'meeting') ?? null;
  const closed = org.stageKind === 'lost' || org.lifecycleStage === 'archived';

  if (!next) {
    if (closed) return null;
    return (
      <Card className="flex flex-wrap items-center gap-3 border-overdue/30 bg-overdue-bg/60 p-4">
        <AlertTriangle className="size-5 text-overdue" />
        <div className="flex-1">
          <div className="font-semibold text-overdue">אין פולואפ עתידי</div>
          <div className="text-sm text-ink-soft">כדי ש{org.lifecycleStage === 'customer' ? 'הלקוח' : 'הליד'} לא ייפול בין הכיסאות — קבעו את הצעד הבא.</div>
        </div>
        {can('crm.write') && (
          <Button onClick={() => setScheduling(true)}>
            <CalendarClock /> קביעת פולואפ
          </Button>
        )}
        <NewTaskDialog open={scheduling} onOpenChange={setScheduling} organizationId={org.id} organizationName={org.name} contacts={org.contacts} />
      </Card>
    );
  }
  return (
    <Card className="flex flex-wrap items-center gap-3 p-4">
      <span className="grid size-10 place-items-center rounded-full bg-matcha-100 text-matcha-800">
        <CalendarClock className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-muted">{next.kind === 'meeting' ? 'הפגישה הבאה' : 'הפולואפ הבא'}</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{next.title}</span>
          <FollowupBadge dueAt={next.dueAt} />
          <span className="text-sm text-muted">{formatDateTime(next.dueAt)}</span>
        </div>
        {next.assigneeName && <div className="text-xs text-muted">אחראי: {next.assigneeName}</div>}
      </div>
      {can('crm.write') && (
        <Button onClick={() => setCompleting(true)}>
          <Check /> בוצע · קביעת הבא
        </Button>
      )}
      <CompleteTaskDialog task={{ ...next, organizationName: org.name }} open={completing} onOpenChange={setCompleting} />
    </Card>
  );
}

function OpenTasks({ org }: { org: OrgDetail }) {
  const [completing, setCompleting] = useState<Task | null>(null);
  if (!org.openTasks.length) return <EmptyState title="אין משימות פתוחות" />;
  return (
    <ul className="divide-y divide-line/70">
      {org.openTasks.map((t) => (
        <li key={t.id} className="flex items-center gap-3 px-4 py-3">
          <Badge variant="outline">{TASK_KIND_LABELS[t.kind]}</Badge>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{t.title}</div>
            <div className="text-xs text-muted">{[formatDateTime(t.dueAt), t.assigneeName, t.contactName, t.location].filter(Boolean).join(' · ')}</div>
          </div>
          <FollowupBadge dueAt={t.dueAt} compact />
          <Button size="sm" variant="secondary" onClick={() => setCompleting(t)}>
            <Check /> בוצע
          </Button>
        </li>
      ))}
      {completing && <CompleteTaskDialog task={{ ...completing, organizationName: org.name }} open onOpenChange={(o) => !o && setCompleting(null)} />}
    </ul>
  );
}

// ─── Side cards ──────────────────────────────────────────────────────────
function ContactsCard({ org }: { org: OrgDetail }) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  return (
    <Card>
      <CardHeader
        title="אנשי קשר"
        icon={<Users />}
        count={org.contacts.length}
        actions={can('crm.write') && <Button variant="ghost" size="sm" onClick={() => setEditing('new')}><Plus /> הוספה</Button>}
      />
      {org.contacts.length === 0 ? (
        <EmptyState title="אין אנשי קשר" />
      ) : (
        <ul className="divide-y divide-line/70">
          {org.contacts.map((c) => (
            <li key={c.id} className="group px-4 py-3">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 font-semibold">
                    {c.fullName}
                    {c.isPrimary && <Star className="size-3.5 fill-gold-400 text-gold-400" aria-label="איש קשר ראשי" />}
                  </div>
                  <div className="text-xs text-muted">{[CONTACT_ROLE_LABELS[c.role], c.position].filter(Boolean).join(' · ')}</div>
                  <div className="mt-1 flex flex-col gap-0.5 text-sm">
                    {c.phoneE164 && <PhoneText value={c.phoneE164} />}
                    {c.email && <span className="ltr truncate text-ink-soft">{c.email}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <ContactActions phone={c.phoneE164} whatsapp={c.whatsappE164} email={c.email} />
                  {can('crm.write') && (
                    <button className="text-xs text-muted opacity-0 group-hover:opacity-100 hover:text-ink focus:opacity-100" onClick={() => setEditing(c)}>
                      עריכה
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ContactDialog orgId={org.id} contact={editing === 'new' ? null : editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} />
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '' || children === false) return null;
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <dt className="w-28 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
    </div>
  );
}

function BusinessCard({ org }: { org: OrgDetail }) {
  return (
    <Card>
      <CardHeader title="פרטי העסק" icon={<Building2 />} />
      <dl className="px-4 py-2">
        <Row label="שם משפטי">{org.legalName}</Row>
        <Row label="ח.פ. / ע.מ.">{org.vatId && <span className="ltr tabular">{org.vatId}</span>}</Row>
        <Row label="סוג עסק">{org.businessType}</Row>
        <Row label="סניפים">{org.branchesCount}</Row>
        <Row label="כתובת">{[org.address, org.city].filter(Boolean).join(', ')}</Row>
        <Row label="אתר">
          {org.website && (
            <a className="ltr text-matcha-700 underline" href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer">
              {org.website}
            </a>
          )}
        </Row>
        <Row label="עדיפות">{PRIORITY_LABELS[org.priority]}</Row>
        <Row label="נוצר">{formatDate(org.createdAt)}</Row>
        {org.notes && <p className="mt-2 rounded-control bg-gold-50 p-2.5 text-sm whitespace-pre-wrap">{org.notes}</p>}
        {!org.legalName && !org.vatId && !org.address && !org.website && !org.notes && (
          <p className="py-2 text-sm text-faint">אפשר להשלים פרטים דרך "עריכת פרטים"</p>
        )}
      </dl>
    </Card>
  );
}

function CommercialCard({ org }: { org: OrgDetail }) {
  return (
    <Card>
      <CardHeader title="מצב מסחרי" icon={<Crown />} />
      <dl className="px-4 py-2">
        <Row label="סטטוס">{LIFECYCLE_LABELS[org.lifecycleStage]}</Row>
        <Row label="לקוח מאז">{org.customerSince && formatDate(org.customerSince)}</Row>
        <Row label="שווי משוער">{org.estimatedValue != null && formatILS(org.estimatedValue, { decimals: false })}</Row>
        <Row label="צריכה חודשית">{org.estimatedMonthlyKg != null && `${org.estimatedMonthlyKg} ק״ג`}</Row>
        <Row label="הנחת לקוח">{org.defaultDiscountPct != null && `${org.defaultDiscountPct}%`}</Row>
        <Row label="תנאי תשלום">{org.paymentTerms}</Row>
        <Row label="תנאי משלוח">{org.shippingTerms}</Row>
        <Row label="סיבת אובדן">{org.lostReason}</Row>
        <Row label="פעילויות">{`${org.activityStats.contacts} אינטראקציות · ${org.activityStats.enquiries} פניות`}</Row>
      </dl>
    </Card>
  );
}

function AttributionCard({ org }: { org: OrgDetail }) {
  const s = org.firstSubmission;
  if (!s) return null;
  return (
    <Card>
      <CardHeader title="מקור הפנייה" icon={<Globe />} />
      <dl className="px-4 py-2 text-sm">
        <Row label="התקבל">{formatDateTime(s.receivedAt)}</Row>
        <Row label="טופס">{s.formName}</Row>
        <Row label="עמוד">{s.pageUrl && <span className="ltr block truncate">{safeDecodeUri(s.pageUrl)}</span>}</Row>
        <Row label="דף נחיתה">{s.landingPage && <span className="ltr block truncate">{safeDecodeUri(s.landingPage)}</span>}</Row>
        <Row label="הפניה מ-">{s.referrer && <span className="ltr block truncate">{s.referrer}</span>}</Row>
        <Row label="utm_source">{s.utmSource}</Row>
        <Row label="utm_medium">{s.utmMedium}</Row>
        <Row label="utm_campaign">{s.utmCampaign}</Row>
        <Row label="utm_content">{s.utmContent}</Row>
        <Row label="utm_term">{s.utmTerm}</Row>
        <Row label="gclid">{s.gclid && <span className="ltr block truncate">{s.gclid}</span>}</Row>
        <Row label="fbclid">{s.fbclid && <span className="ltr block truncate">{s.fbclid}</span>}</Row>
        <Row label="ttclid">{s.ttclid && <span className="ltr block truncate">{s.ttclid}</span>}</Row>
        <Row label="מוצר">{s.productInterest}</Row>
        <Row label="כמות משוערת">{s.expectedQuantity}</Row>
      </dl>
    </Card>
  );
}

